import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  CalendarDaysIcon,
  UserGroupIcon,
  CreditCardIcon,
  ChevronDownIcon,
  CheckIcon,
} from "@heroicons/react/24/outline";
import api from "../../api";
import Loader from "../../components/Loader";

function AddSubscription() {
  const [jewellers, setJewellers] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [form, setForm] = useState({
    jeweller_id: "",
    start_date: "",
    expiry_date: "",
    status: "active",
  });
  const [loading, setLoading] = useState(true);
  const [openDropdown, setOpenDropdown] = useState(false);
  const [openStatusDropdown, setOpenStatusDropdown] = useState(false);

  const navigate = useNavigate();
  const jewellerDropdownRef = useRef(null);
  const statusDropdownRef = useRef(null);

  useEffect(() => {
    fetchData();

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const handleClickOutside = (e) => {
    if (
      jewellerDropdownRef.current &&
      !jewellerDropdownRef.current.contains(e.target)
    ) {
      setOpenDropdown(false);
    }
    if (
      statusDropdownRef.current &&
      !statusDropdownRef.current.contains(e.target)
    ) {
      setOpenStatusDropdown(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [jewellersRes, subscriptionsRes] = await Promise.all([
        api.get("/jewellers"),
        api.get("/subscriptions"),
      ]);
      setJewellers(jewellersRes.data);
      setSubscriptions(subscriptionsRes.data);
    } catch (err) {
      console.error(err.response || err.message);
      alert("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const availableJewellers = jewellers.filter(
    (j) =>
      !subscriptions.some(
        (sub) => sub.jeweller_id === j.id && sub.status === "active"
      )
  );

  const selectedJeweller = availableJewellers.find(
    (j) => j.id === parseInt(form.jeweller_id)
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (new Date(form.expiry_date) < new Date(form.start_date))
      return alert("❌ Expiry date cannot be earlier than start date");
    if (!form.jeweller_id) return alert("Please select a jeweller");

    try {
      await api.post("/subscriptions", form);
      alert("✅ Subscription added successfully!");
      navigate("/subscriptions");
    } catch (err) {
      console.error(err.response || err.message);
      alert("Failed to add subscription");
    }
  };

  if (loading) return <Loader />;

  return (
    <div className="bg-gray-50 flex justify-center p-6">
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-lg p-8">
        {/* Header */}
        <div className="flex items-center mb-6 gap-2">
          <CreditCardIcon className="h-7 w-7 text-indigo-600" />
          <h1 className="text-2xl font-bold text-gray-800">
            Add Subscription
          </h1>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Custom Jeweller Dropdown */}
          <div className="relative" ref={jewellerDropdownRef}>
            <label className="absolute -top-2 left-3 bg-white px-1 text-gray-500 text-sm flex items-center gap-1">
              <UserGroupIcon className="h-4 w-4 text-indigo-500" /> Select Jeweller
            </label>

            <button
              type="button"
              onClick={() => setOpenDropdown(!openDropdown)}
              className="w-full text-left border border-gray-300 rounded-xl px-4 py-3 flex justify-between items-center text-gray-700 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            >
              {selectedJeweller ? (
                <span>
                  {selectedJeweller.user?.name} ({selectedJeweller.business_name})
                </span>
              ) : (
                <span className="text-gray-400">-- Select --</span>
              )}
              <ChevronDownIcon
                className={`h-5 w-5 text-gray-400 transition-transform ${
                  openDropdown ? "rotate-180" : ""
                }`}
              />
            </button>

            {openDropdown && (
              <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg max-h-56 overflow-y-auto animate-fadeIn">
                {availableJewellers.length > 0 ? (
                  availableJewellers.map((j) => (
                    <div
                      key={j.id}
                      onClick={() => {
                        setForm({ ...form, jeweller_id: j.id });
                        setOpenDropdown(false);
                      }}
                      className={`px-4 py-2 cursor-pointer flex justify-between items-center hover:bg-indigo-50 ${
                        form.jeweller_id === j.id
                          ? "bg-indigo-50 text-indigo-600 font-medium"
                          : "text-gray-700"
                      }`}
                    >
                      <span>
                        {j.user?.name} ({j.business_name})
                      </span>
                      {form.jeweller_id === j.id && (
                        <CheckIcon className="h-4 w-4 text-indigo-600" />
                      )}
                    </div>
                  ))
                ) : (
                  <div className="px-4 py-2 text-gray-500">
                    No jewellers available
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Start & Expiry Dates */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="relative">
              <label className="absolute -top-2 left-3 bg-white px-1 text-gray-500 text-sm flex items-center gap-1">
                <CalendarDaysIcon className="h-4 w-4 text-indigo-500" /> Start Date
              </label>
              <input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-1 focus:ring-indigo-500 text-gray-700"
                required
              />
            </div>

            <div className="relative">
              <label className="absolute -top-2 left-3 bg-white px-1 text-gray-500 text-sm flex items-center gap-1">
                <CalendarDaysIcon className="h-4 w-4 text-indigo-500" /> Expiry Date
              </label>
              <input type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })}
                className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-1 focus:ring-indigo-500 text-gray-700"
                required
              />
            </div>
          </div>

          {/* Custom Status Dropdown */}
          <div className="relative" ref={statusDropdownRef}>
            <label className="absolute -top-2 left-3 bg-white px-1 text-gray-500 text-sm">
              Status
            </label>

            <button type="button" onClick={() => setOpenStatusDropdown(!openStatusDropdown)}
              className="w-full text-left border border-gray-300 rounded-xl px-4 py-3 flex justify-between items-center text-gray-700 focus:outline-none focus:ring-1 focus:ring-indigo-500">
              <span className="capitalize">
                {form.status ? form.status : "-- Select --"}
              </span>
              <ChevronDownIcon className={`h-5 w-5 text-gray-400 transition-transform ${openStatusDropdown ? "rotate-180" : ""}`}/>
            </button>

            {openStatusDropdown && (
              <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg animate-fadeIn">
                {["active", "inactive", "cancelled"].map((status) => (
                  <div
                    key={status}
                    onClick={() => {
                      setForm({ ...form, status });
                      setOpenStatusDropdown(false);
                    }}
                    className={`px-4 py-2 cursor-pointer flex justify-between items-center hover:bg-indigo-50 ${
                      form.status === status
                        ? "bg-indigo-50 text-indigo-600 font-medium"
                        : "text-gray-700"
                    }`}
                  >
                    <span className="capitalize">
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </span>
                    {form.status === status && (
                      <CheckIcon className="h-4 w-4 text-indigo-600" />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3 rounded-xl shadow-md transition-all duration-200"
          >
            Add Subscription
          </button>
        </form>
      </div>
    </div>
  );
}

export default AddSubscription;
